import React, { useState, useEffect } from 'react';
import { apiService, Session, ConversationTurn } from '../services/api';
import logger from '../utils/logger';

function SessionHistory() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [sessionHistories, setSessionHistories] = useState<Map<string, ConversationTurn[]>>(new Map());
  const [loadingHistories, setLoadingHistories] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const sessionsPerPage = 10;
  
  // Date range filter state
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [feedbackRating, setFeedbackRating] = useState<'correct' | 'incorrect' | 'mixed' | ''>('');
  const [feedbackSummary, setFeedbackSummary] = useState<{ correct: number; incorrect: number; mixed: number }>({
    correct: 0,
    incorrect: 0,
    mixed: 0
  });

  useEffect(() => {
    loadSessions();
  }, [startDate, endDate, feedbackRating]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getAllSessions(
        startDate, 
        endDate, 
        feedbackRating || undefined
      );
      setSessions(result.sessions);
      setFeedbackSummary(result.feedbackSummary);
      logger.info('Sessions loaded:', result.sessions.length);
    } catch (err: any) {
      logger.error('Failed to load sessions:', err);
      setError(err.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const toggleSession = async (sessionId: string) => {
    const newExpanded = new Set(expandedSessions);
    
    if (expandedSessions.has(sessionId)) {
      // Collapse
      newExpanded.delete(sessionId);
      setExpandedSessions(newExpanded);
    } else {
      // Expand and load history if not already loaded
      newExpanded.add(sessionId);
      setExpandedSessions(newExpanded);
      
      if (!sessionHistories.has(sessionId)) {
        await loadSessionHistory(sessionId);
      }
    }
  };

  const loadSessionHistory = async (sessionId: string) => {
    try {
      setLoadingHistories(prev => new Set(prev).add(sessionId));
      const history = await apiService.getConversationHistory(sessionId);
      setSessionHistories(prev => new Map(prev).set(sessionId, history.history));
      logger.info(`History loaded for session ${sessionId}:`, history.history.length, 'turns');
    } catch (err: any) {
      logger.error(`Failed to load history for session ${sessionId}:`, err);
    } finally {
      setLoadingHistories(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const parseIntents = (intentsJson: string) => {
    try {
      const parsed = JSON.parse(intentsJson);
      // Ensure we always return an array
      if (Array.isArray(parsed)) {
        return parsed;
      }
      // If it's an object with an 'intents' property that's an array, return that
      if (parsed && Array.isArray(parsed.intents)) {
        return parsed.intents;
      }
      return [];
    } catch {
      return [];
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(sessions.length / sessionsPerPage);
  const startIndex = (currentPage - 1) * sessionsPerPage;
  const endIndex = startIndex + sessionsPerPage;
  const currentSessions = sessions.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(page);
    setExpandedSessions(new Set()); // Collapse all when changing pages
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading sessions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error: {error}</p>
        <button
          onClick={loadSessions}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setFeedbackRating('');
    setCurrentPage(1);
  };

  const handleFeedbackFilter = (rating: 'correct' | 'incorrect' | 'mixed') => {
    if (feedbackRating === rating) {
      // If already selected, clear the filter
      setFeedbackRating('');
    } else {
      // Set the new filter
      setFeedbackRating(rating);
    }
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Session History</h2>
          <p className="text-sm text-gray-500 mt-1">
            Showing {sessions.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, sessions.length)} of {sessions.length} sessions
          </p>
        </div>
        <button
          onClick={loadSessions}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Filter by Created Date Range</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {(startDate || endDate || feedbackRating) && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 transition-colors"
            >
              Clear All Filters
            </button>
          )}
        </div>
      </div>

      {/* Feedback Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">Feedback Summary</h3>
          {feedbackRating && (
            <span className="text-xs text-gray-500 italic">
              Filtered by: <span className="font-semibold capitalize">{feedbackRating}</span>
            </span>
          )}
        </div>
        <div className="grid grid-cols-3 gap-4">
          <button
            onClick={() => handleFeedbackFilter('correct')}
            className={`rounded-lg p-3 border transition-all ${
              feedbackRating === 'correct'
                ? 'bg-green-100 border-green-400 shadow-md ring-2 ring-green-400'
                : 'bg-green-50 border-green-200 hover:bg-green-100 hover:border-green-300 hover:shadow-md cursor-pointer'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-2xl">✅</span>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-800">{feedbackSummary.correct}</div>
                <div className="text-xs text-green-600 font-medium">Correct</div>
              </div>
            </div>
          </button>
          <button
            onClick={() => handleFeedbackFilter('incorrect')}
            className={`rounded-lg p-3 border transition-all ${
              feedbackRating === 'incorrect'
                ? 'bg-red-100 border-red-400 shadow-md ring-2 ring-red-400'
                : 'bg-red-50 border-red-200 hover:bg-red-100 hover:border-red-300 hover:shadow-md cursor-pointer'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-2xl">❌</span>
              <div className="text-right">
                <div className="text-2xl font-bold text-red-800">{feedbackSummary.incorrect}</div>
                <div className="text-xs text-red-600 font-medium">Incorrect</div>
              </div>
            </div>
          </button>
          <button
            onClick={() => handleFeedbackFilter('mixed')}
            className={`rounded-lg p-3 border transition-all ${
              feedbackRating === 'mixed'
                ? 'bg-yellow-100 border-yellow-400 shadow-md ring-2 ring-yellow-400'
                : 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100 hover:border-yellow-300 hover:shadow-md cursor-pointer'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-2xl">⚠️</span>
              <div className="text-right">
                <div className="text-2xl font-bold text-yellow-800">{feedbackSummary.mixed}</div>
                <div className="text-xs text-yellow-600 font-medium">Mixed</div>
              </div>
            </div>
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3 text-center italic">
          Click a card to filter sessions by feedback rating
        </p>
      </div>

      {sessions.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <p className="text-gray-500">No sessions with conversation history found</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {currentSessions.map((session, idx) => (
            <div key={session.sessionId || `session-${idx}`} className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Session Header */}
              <button
                onClick={() => session.sessionId && toggleSession(session.sessionId)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-lg">
                    {session.sessionId && expandedSessions.has(session.sessionId) ? '📂' : '📁'}
                  </span>
                  <div className="text-left">
                    <div className="flex items-center space-x-2">
                      <span className="font-mono text-xs text-gray-700">
                        {session.sessionId ? `${session.sessionId.slice(0, 12)}...` : 'Unknown'}
                      </span>
                      <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                        session.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {session.status || 'unknown'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 space-y-0.5">
                      {session.created_at && <div>Created: {formatDate(session.created_at)}</div>}
                      {session.ip_address && <div>IP: {session.ip_address}</div>}
                    </div>
                  </div>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    session.sessionId && expandedSessions.has(session.sessionId) ? 'transform rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Session History (Expanded) */}
              {session.sessionId && expandedSessions.has(session.sessionId) && (
                <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
                  {loadingHistories.has(session.sessionId) ? (
                    <div className="text-center py-3">
                      <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      <p className="mt-1 text-xs text-gray-600">Loading...</p>
                    </div>
                  ) : sessionHistories.get(session.sessionId)?.length === 0 ? (
                    <p className="text-gray-500 text-xs">No conversation turns in this session</p>
                  ) : (
                    <div className="space-y-2">
                      {sessionHistories.get(session.sessionId)?.map((turn, index) => (
                        <div key={turn.id} className="bg-white rounded p-3 border border-gray-200">
                          <div className="flex items-start justify-between mb-2">
                            <span className="text-xs font-medium text-gray-500">
                              Turn #{index + 1}
                            </span>
                            {turn.timestamp && (
                              <span className="text-xs text-gray-400">
                                {formatDate(turn.timestamp)}
                              </span>
                            )}
                          </div>
                          
                          {/* User Input */}
                          <div className="mb-2">
                            <div className="text-xs font-semibold text-gray-700 mb-1">Input:</div>
                            <div className="text-xs text-gray-900 bg-blue-50 rounded p-2">
                              {turn.userInput}
                            </div>
                          </div>

                          {/* Context (if any) */}
                          {turn.context && (
                            <div className="mb-2">
                              <div className="text-xs font-semibold text-gray-700 mb-1">Context:</div>
                              <div className="text-xs text-gray-700 bg-yellow-50 rounded p-2">
                                {turn.context}
                              </div>
                            </div>
                          )}

                          {/* Classified Intents */}
                          <div className="mb-2">
                            <div className="text-xs font-semibold text-gray-700 mb-1">Response:</div>
                            <div className="space-y-1">
                              {parseIntents(turn.classifiedIntents).map((intent: any, idx: number) => (
                                <div key={idx} className="text-xs bg-green-50 rounded p-2">
                                  <span className="font-medium text-green-800">Action:</span> {intent.action}
                                  {' • '}
                                  <span className="font-medium text-green-800">Object:</span> {intent.object}
                                  {intent.modifier && (
                                    <>
                                      {' • '}
                                      <span className="font-medium text-green-800">Modifier:</span> {intent.modifier}
                                    </>
                                  )}
                                </div>
                              ))}
                              {parseIntents(turn.classifiedIntents).length === 0 && (
                                <div className="text-xs text-gray-500 italic">No intents classified</div>
                              )}
                            </div>
                          </div>

                          {/* Feedback */}
                          {turn.feedback_rating && (
                            <div className="mb-2 pt-2 border-t border-gray-200">
                              <div className="text-xs font-semibold text-gray-700 mb-1">Feedback:</div>
                              <div className="text-xs bg-purple-50 rounded p-2">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="text-lg">
                                    {turn.feedback_rating === 'correct' ? '✅' : turn.feedback_rating === 'incorrect' ? '❌' : '⚠️'}
                                  </span>
                                  <span className="font-medium text-purple-800 capitalize">
                                    {turn.feedback_rating}
                                  </span>
                                  {turn.feedback_timestamp && (
                                    <span className="text-gray-500">
                                      • {formatDate(turn.feedback_timestamp)}
                                    </span>
                                  )}
                                </div>
                                {turn.feedback_comment && (
                                  <div className="text-gray-700 mt-1 italic">
                                    "{turn.feedback_comment}"
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Token Usage & Cost */}
                          {(turn.openai_total_tokens !== undefined && turn.openai_total_tokens > 0) && (
                            <div className="pt-2 border-t border-gray-200">
                              <div className="flex items-center justify-between text-xs">
                                <div className="flex items-center space-x-3 text-gray-600">
                                  <span>
                                    <span className="font-medium">Tokens:</span> {turn.openai_total_tokens}
                                  </span>
                                  <span className="text-gray-400">|</span>
                                  <span>
                                    <span className="font-medium">Prompt:</span> {turn.openai_prompt_tokens || 0}
                                  </span>
                                  <span className="text-gray-400">|</span>
                                  <span>
                                    <span className="font-medium">Completion:</span> {turn.openai_completion_tokens || 0}
                                  </span>
                                </div>
                                {turn.openai_cost !== undefined && Number(turn.openai_cost) > 0 && (
                                  <span className="font-medium text-blue-600">
                                    ${Number(turn.openai_cost).toFixed(6)}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center space-x-2 mt-4">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded text-sm ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Previous
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`px-3 py-1 rounded text-sm ${
                    currentPage === page
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded text-sm ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SessionHistory;

